import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { getKnowledgeGraph, listDomains } from "@/lib/academy.functions";
import { AcademyNav } from "@/components/academy/AcademyNav";
import { ACADEMY_ID, breadcrumbs, ldScript, orgRef, pageNode, personRef, seoMeta } from "@/lib/schema";

const TITLE = "Knowledge Graph — Buddha KA Academy";
const DESCRIPTION =
  "See how every Academy lesson connects — prerequisites, related concepts, and advanced paths — rendered as an interactive map of the curriculum.";

export const Route = createFileRoute("/academy/graph")({
  head: () => ({
    ...seoMeta({ title: TITLE, description: DESCRIPTION, path: "/academy/graph" }),
    scripts: [
      ldScript(
        pageNode({
          type: "WebPage",
          id: "https://buddhaka.com/academy/graph#graph",
          url: "https://buddhaka.com/academy/graph",
          name: TITLE,
          description: DESCRIPTION,
          about: [personRef(), orgRef()],
          extra: { isPartOf: { "@id": ACADEMY_ID } },
        }),
      ),
      ldScript(breadcrumbs([["Academy", "/academy"], ["Knowledge Graph", "/academy/graph"]])),
    ],
  }),
  component: GraphPage,
});


const W = 900;
const H = 620;

function GraphPage() {
  const domains = useQuery({ queryKey: ["academy", "domains"], queryFn: () => listDomains() });
  const [domain, setDomain] = useState<string>("human-design");
  const graph = useQuery({
    queryKey: ["academy", "graph", domain],
    queryFn: () => getKnowledgeGraph({ data: { domainSlug: domain } }),
  });
  const [hover, setHover] = useState<string | null>(null);

  const layout = useMemo(() => {
    const nodes = graph.data?.nodes ?? [];
    const n = Math.max(nodes.length, 1);
    const cx = W / 2, cy = H / 2;
    const rings: Record<string, number> = { beginner: 130, intermediate: 220, advanced: 290, master: 340 };
    return nodes.map((node, i) => {
      const r = rings[node.difficulty] ?? 240;
      const angle = (i / n) * Math.PI * 2;
      return { ...node, x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r };
    });
  }, [graph.data]);

  const pos = useMemo(() => new Map(layout.map((n) => [n.id, n])), [layout]);
  const active = hover ? pos.get(hover) : null;

  return (
    <>
      <AcademyNav />
      <div className="min-h-dvh bg-obsidian text-foreground">
        <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-gold">Knowledge Graph</p>
              <h1 className="mt-2 font-serif text-4xl text-gold-luminous">Every concept, connected.</h1>
            </div>
            <select aria-label="Filter graph by domain" value={domain} onChange={(e) => setDomain(e.target.value)}
              className="rounded-full border hairline bg-black/40 px-4 py-2 text-sm">
              {(domains.data ?? []).map((d) => <option key={d.id} value={d.slug}>{d.name}</option>)}
            </select>
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-[1fr_320px]">
            <div className="rounded-2xl border hairline bg-black/40 p-2 overflow-hidden">
              <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[70vh]" role="img" aria-label="Concept network">
                <defs>
                  <radialGradient id="core" cx="50%" cy="50%">
                    <stop offset="0%" stopColor="rgba(230,190,90,0.35)" />
                    <stop offset="100%" stopColor="rgba(230,190,90,0)" />
                  </radialGradient>
                </defs>
                <circle cx={W / 2} cy={H / 2} r={340} fill="url(#core)" />
                {(graph.data?.edges ?? []).map((e, i) => {
                  const s = pos.get(e.source), t = pos.get(e.target);
                  if (!s || !t) return null;
                  const stroke = e.kind === "prereq" ? "rgba(230,190,90,0.35)" : "rgba(255,255,255,0.08)";
                  return <line key={i} x1={s.x} y1={s.y} x2={t.x} y2={t.y} stroke={stroke} strokeWidth={e.kind === "prereq" ? 1 : 0.6} />;
                })}
                {layout.map((n) => (
                  <g key={n.id} onMouseEnter={() => setHover(n.id)} onFocus={() => setHover(n.id)}
                    tabIndex={0} className="cursor-pointer outline-none">
                    <circle cx={n.x} cy={n.y} r={hover === n.id ? 9 : 5}
                      fill={hover === n.id ? "#f5d67a" : "rgba(230,190,90,0.7)"}
                      stroke="rgba(0,0,0,0.6)" strokeWidth={1} />
                  </g>
                ))}
              </svg>
            </div>
            <aside className="rounded-2xl border hairline p-5">
              <h2 className="font-serif text-xl text-gold-luminous">
                {active ? active.title : "Hover a node"}
              </h2>
              {active ? (
                <>
                  <p className="mt-1 text-xs uppercase tracking-widest text-gold">{active.difficulty} · {active.tier}</p>
                  <Link to="/academy/$domain/lesson/$lesson"
                    params={{ domain, lesson: active.slug }}
                    className="mt-4 inline-flex items-center rounded-full bg-gold-gradient px-4 py-2 text-xs font-semibold text-obsidian">
                    Open lesson
                  </Link>
                </>
              ) : (
                <p className="mt-3 text-sm text-foreground/70">
                  Inner ring: beginner. Outer rings: advanced. Gold lines are prerequisites.
                </p>
              )}
              <p className="mt-6 text-xs text-foreground/50">
                {layout.length} concepts · {(graph.data?.edges ?? []).length} connections
              </p>
            </aside>
          </div>
        </section>
      </div>
    </>
  );
}
